import { Injectable } from '@angular/core';
import { CommonHttpService } from '../../shared/services/http.service';
import { Response } from '@angular/http';
import { OauthService } from '../../shared/services/oauth.service';
import { RequestTokenParams } from '../../shared/models/request-token-params.model';
import { PaymentStepsService } from './payment-steps.service';
import { AuthenticatedUserRepositoryService } from './authenticated-user-repository.service';
import { UserWasChangedListenerInterface } from '../../shared/contracts/user-was-changed-listener.interface';
import { UnauthenticatedUserRepositoryService } from './unauthenticated-user-repository.service';
import { Observable } from 'rxjs/Observable';
import { ClientModel } from '../../shared/models/client.model';
import { FacebookUserModel } from '../../shared/models/facebook.user.model';
import { GoogleUserModel } from '../../shared/models/google.user.model';
import { isNullOrUndefined } from 'util';
import { FormGroup } from '@angular/forms';

@Injectable()
export class IdentificationStepsService implements UserWasChangedListenerInterface {

    constructor(
        protected http: CommonHttpService,
        protected oauthService: OauthService,
        protected authenticatedUserRepository: AuthenticatedUserRepositoryService,
        protected unauthenticatedUserRepository: UnauthenticatedUserRepositoryService
    ) {
        this.authenticatedUserRepository.changedUser$.subscribe(user => {
            this.userWasChanged(user);
        });
    }

    /**
     * 'userWasChanged' event listener
     **/
    userWasChanged(user) {
        if (user !== null) {
            this.unauthenticatedUserRepository.setEmail(user.email);
            this.unauthenticatedUserRepository.setEmailExists(true);
        } else {
            this.resetEmail();
        }
    }

    /**
     * Return stored value from email existence.
     **/
    public existentEmail() {
        return this.unauthenticatedUserRepository.exists();
    }
    /**
     * Verify if has some inputted email.
     **/
    public wasInputtedEmail(): boolean {
        return this.unauthenticatedUserRepository.getEmail() !== '';
    }
    /**
     * Verify if has some identified user.
     **/
    public wasIdentifiedUser(): boolean {
        return this.authenticatedUserRepository.getUser() !== null;
    }
    /**
     * Call API to verify if exists inputted email.
     **/
    public verifyEmailExists(email: string) {
        return new Observable(observable => {
            this.http.get(
                `/ecommerce/identification/exists/email?value=${email}`
            ).subscribe((res) => {
                this.unauthenticatedUserRepository.setEmail(email);
                if (res.status === 200) {
                    if (res.json().exists) {
                        this.unauthenticatedUserRepository.setEmailExists(true);
                        this.unauthenticatedUserRepository.setEmail(email);
                    } else {
                        this.resetEmail();
                        this.unauthenticatedUserRepository.setEmailExists(false);
                        this.unauthenticatedUserRepository.setEmail(email);
                    }
                    observable.next(res);
                }
                observable.complete();
            }, () => {
                this.resetEmail();
                this.unauthenticatedUserRepository.setEmailExists(false);
                this.unauthenticatedUserRepository.setEmail(email);
                observable.next(null);
                observable.complete();
            });
        });
    }
    /**
     * Reset inputted email.
     **/
    public resetEmail() {
        this.unauthenticatedUserRepository.reset();
    }
    /**
     * Reset inputted user.
     **/
    public resetUser() {
        this.authenticatedUserRepository.setUser(null);
        this.oauthService.resetToken();
    }
    /**
     * Call API to make login with email and password.
     **/
    public login(password: string, email?: string) {
        const params = new RequestTokenParams();
        params.grant_type = 'password';
        params.password = password;
        params.username = (email || this.unauthenticatedUserRepository.getEmail());

        return new Observable(observable => {
            this.oauthService
                .requestToken(params)
                .subscribe((requests) => {
                    requests.subscribe((user) => {
                        this.authenticatedUserRepository.setUser(user);
                        this.userWasChanged(user);
                        observable.next(user);
                        observable.complete();
                    });
                }, (err: Response) => {
                    this.oauthService.triggerTokenRefused();
                    observable.error(err);
                });
        });
    }
    /**
     * Call API to make login with social user credentials.
     **/
    public socialLogin(socialUser: FacebookUserModel|GoogleUserModel, provider: string) {
        const params = new RequestTokenParams();
        params.grant_type = 'social';
        params.provider = provider;
        params.access_token = socialUser.uid;

        return new Observable(observable => {
            this.oauthService
                .requestToken(params)
                .subscribe(
                    (requests) => {
                        requests.subscribe((user) => {
                            this.authenticatedUserRepository.setUser(user);
                            this.userWasChanged(user);
                            observable.next(user);
                            observable.complete();
                        });
                    },
                    () => {
                        this.newSocialUser(socialUser, provider);
                        this.verifyEmailExists(socialUser.email).subscribe(
                            (res: any) => {
                                if (res.json().exists) {
                                    this.createSocialUserToClientByEmail(
                                        socialUser,
                                        provider
                                    ).subscribe(() => {
                                        this.socialLogin(
                                            socialUser,
                                            provider
                                        ).subscribe();
                                    });
                                } else {
                                    observable.next(socialUser);
                                    observable.complete();
                                }
                            }
                        );
                    }
                );
        });
    }
    /**
     * Call API to make login using only token.
     **/
    public onlyTokenLogin(token: string) {
        const params = new RequestTokenParams();
        params.grant_type = 'password_recovery_token';
        params.access_token = token;

        return new Observable(observable => {
            this.oauthService
                .requestToken(params)
                .subscribe((requests) => {
                    requests.subscribe((user) => {
                        this.authenticatedUserRepository.setUser(user);
                        this.authenticatedUserRepository.setLoggedWithPassword(false);
                        this.userWasChanged(user);
                        observable.next(user);
                        observable.complete();
                    });
                }, (err: Response) => {
                    this.oauthService.triggerTokenRefused();
                    observable.error(err.json());
                });
        });
    }
    /**
     * Call API to create client.
     **/
    public create(
        client: ClientModel,
        socialUser: FacebookUserModel|GoogleUserModel = null
    ) {
        return new Observable(observable => {
            this.http.post('/ecommerce/identification', client).subscribe(
                (res: Response) => {
                    if (res.status === 201) {
                        this.userWasChanged(res.json().data);
                        if (!isNullOrUndefined(socialUser)) {
                            this.socialLogin(
                                socialUser,
                                client.social_network
                            ).subscribe(() => {
                                observable.next(res.json().data);
                                observable.complete();
                            });
                        } else {
                            this.login(
                                client.password,
                                client.email
                            ).subscribe(() => {
                                observable.next(res.json().data);
                                observable.complete();
                            });
                        }
                    }
                }, (err: Response) => {
                    observable.error(err.json());
                }
            );
        });
    }

    /**
     * This unauthenticated user reset data and set new social user data.
     **/
    private newSocialUser(
        socialUser: FacebookUserModel|GoogleUserModel,
        provider: string
    ) {
        const names = socialUser.name.split(' ');
        this.unauthenticatedUserRepository.setName1(names.shift());
        this.unauthenticatedUserRepository.setName2(names.join(' '));
        this.unauthenticatedUserRepository.setSocialNetwork(provider);
        this.unauthenticatedUserRepository.setSocialUser(socialUser);
        this.unauthenticatedUserRepository.setSocialUid(socialUser.uid);
        this.oauthService.triggerTokenRefused();
    }
    /**
     * This found user by email and create a social user for them.
     **/
    private createSocialUserToClientByEmail(
        socialUser: FacebookUserModel|GoogleUserModel,
        provider: string
    ): Observable<any> {
        return new Observable(observable => {
            this.http.post('/ecommerce/identification/social-user', {
                email: socialUser.email,
                uid: socialUser.uid,
                network: provider
            }).subscribe(
                () => {
                    observable.next(null);
                    observable.complete();
                },
                () => {
                    observable.next(null);
                    observable.complete();
                }
            );
        });
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src3048926480/src/src/app/payment/services/identification-steps.service.ts